import { useCallback, useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { object as yupObject } from 'yup'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useYupFields } from 'hooks/forms/useYupFields'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import { useAllowedCountriesQuery } from 'hooks/queries/useAllowedCountriesQuery'
import { CustomerEntityType, type CreateCustomerAgreement } from 'types/customers'
import type { ErrorDetails } from 'types/types'
import { getCountryOptions } from 'utils/forms'
import { BUSINESS_USE_CASES } from '../helpers/constants'
import { findLabel, getOptions } from '../utils/utils'

const DEFAULT_VALUES: CreateCustomerAgreement = {
  reference: '',
  useCase: '',
  customerType: 'COMPANY',
  countryCode: '',
  customerName: '',
}

export const useAddCustomerAgreementForm = (
  defaultValues?: CreateCustomerAgreement,
  serverErrors?: ErrorDetails,
  customerType?: string
) => {
  const { t } = useTranslation()
  const { getRequiredYupField } = useYupFields()
  const isIndividual = customerType === CustomerEntityType.INDIVIDUAL

  const { data: countries = [], isFetching: isFetchingCountries } = useAllowedCountriesQuery()
  const countryOptions = useMemo(() => getCountryOptions(countries), [countries])
  const useCaseOptions = useMemo(
    () => getOptions(BUSINESS_USE_CASES, 'customers.useCaseOptions', t),
    [t]
  )

  const customerNameLabel = isIndividual ? t('customers.fullName') : t('customers.businessName')

  const formSchema = useMemo(
    () =>
      yupObject({
        reference: getRequiredYupField({ label: t('customers.externalReference') }),
        customerName: getRequiredYupField({ label: customerNameLabel }),
        useCase: getRequiredYupField({ label: t('customers.useCase') }),
        countryCode: getRequiredYupField({ label: t('customers.countryCode') }),
      }),
    [customerNameLabel, t, getRequiredYupField]
  )

  const form = useForm<CreateCustomerAgreement>({
    defaultValues: defaultValues ?? DEFAULT_VALUES,
    mode: 'onChange',
    resolver: yupFormResolver<CreateCustomerAgreement>(formSchema),
  })

  const { handleSubmit, control, setError } = form
  const formValues = useWatch({ control })

  useEffect(() => {
    if (!serverErrors) return
    Object.entries(serverErrors).forEach(([field, messages]) => {
      setError(field as keyof CreateCustomerAgreement, { message: messages[0] })
    })
  }, [serverErrors, setError])
  const hasErrors = !!Object.keys(form.formState.errors).length

  const { setInputField, setComboboxField } = useFormFields<keyof CreateCustomerAgreement>(
    formSchema,
    form,
    formValues
  )

  const buildDisplayData = useCallback(
    (data: CreateCustomerAgreement) => ({
      useCase: findLabel(useCaseOptions, data.useCase),
      countryCode: findLabel(countryOptions, data.countryCode),
    }),
    [useCaseOptions, countryOptions]
  )

  return {
    formValues,
    formSchema,
    hasErrors,
    handleSubmit,
    setInputField,
    setComboboxField,
    useCaseOptions,
    countryOptions,
    isFetchingCountries,
    buildDisplayData,
  }
}
